import React from "react";
import { Handle, Position } from "@xyflow/react";
import { Play, HelpCircle, CheckCircle2, Wrench, ShoppingCart, Package, Type, AlertTriangle } from "lucide-react";
import { usePricebook } from "../../contexts/PricebookContext";

const baseCard = "rounded-lg border bg-white shadow-sm px-3 py-2 min-w-[200px] max-w-[260px]";

function RequiredBadge({ show }) {
  if (!show) return null;
  return (
    <span className="ml-1.5 inline-flex items-center text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
      Required
    </span>
  );
}

export function InputNode({ data, selected }) {
  return (
    <div className={`${baseCard} ${selected ? "ring-2 ring-blue-500" : ""}`} style={{ borderColor: "#0D9488", background: "#F0FDFA" }}>
      <Handle type="target" position={Position.Top} className="!bg-teal-500" />
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-teal-100 flex items-center justify-center">
          <Type className="h-4 w-4 text-teal-600" />
        </div>
        <div className="min-w-0 flex items-center">
          <div className="text-[10px] uppercase tracking-wider text-teal-700 font-semibold">Input</div>
          <RequiredBadge show={data.required} />
        </div>
      </div>
      <div className="mt-1.5 text-[13px] font-semibold text-slate-900">{data.label || "Enter details"}</div>
      <div className="mt-1.5 h-7 rounded-md border border-dashed border-teal-300 bg-white/70 px-2 flex items-center text-[11px] text-slate-400 italic">
        {data.placeholder || "Type answer…"}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-teal-500" />
    </div>
  );
}

export function StartNode({ data, selected }) {
  return (
    <div className={`${baseCard} ${selected ? "ring-2 ring-blue-500" : ""}`} style={{ borderColor: "#10B981" }}>
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-emerald-50 flex items-center justify-center">
          <Play className="h-3.5 w-3.5 text-emerald-600" fill="currentColor" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Start</div>
          <div className="text-[13px] font-semibold text-slate-900 truncate">{data.label || "Start"}</div>
        </div>
      </div>
      {data.description && (
        <div className="mt-1.5 text-[11.5px] text-slate-500">{data.description}</div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-emerald-500" />
    </div>
  );
}

export function QuestionNode({ data, selected }) {
  const choices = (data.choices && data.choices.length ? data.choices : ["Yes", "No"]).slice(0, 4);
  return (
    <div className={`${baseCard} ${selected ? "ring-2 ring-blue-500" : ""}`} style={{ borderColor: "#2563EB" }}>
      <Handle type="target" position={Position.Top} className="!bg-blue-500" />
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-blue-50 flex items-center justify-center">
          <HelpCircle className="h-4 w-4 text-blue-600" />
        </div>
        <div className="min-w-0 flex items-center">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Question</div>
          <RequiredBadge show={data.required} />
        </div>
      </div>
      <div className="mt-1.5 text-[13px] font-semibold text-slate-900">{data.label || "Question?"}</div>
      <div className="mt-2 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(choices.length, 2)}, minmax(0, 1fr))` }}>
        {choices.map((c, i) => {
          const leftPct = ((i + 1) / (choices.length + 1)) * 100;
          return (
            <div key={`${c}-${i}`} className="relative flex justify-center">
              <span className="inline-block text-[10.5px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 truncate max-w-full">
                {c}
              </span>
              <Handle
                type="source"
                position={Position.Bottom}
                id={c}
                style={{ left: `${leftPct}%`, bottom: -8, background: "#2563EB", width: 8, height: 8 }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ActionNode({ data, selected }) {
  return (
    <div className={`${baseCard} ${selected ? "ring-2 ring-blue-500" : ""}`} style={{ borderColor: "#F59E0B" }}>
      <Handle type="target" position={Position.Top} className="!bg-amber-500" />
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-amber-50 flex items-center justify-center">
          <Wrench className="h-4 w-4 text-amber-600" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center">Action<RequiredBadge show={data.required} /></div>
          <div className="text-[13px] font-semibold text-slate-900 truncate">{data.label || "Action"}</div>
        </div>
      </div>
      {data.description && (
        <div className="mt-1.5 text-[11.5px] text-slate-500">{data.description}</div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-amber-500" />
    </div>
  );
}

export function DiagnosisNode({ data, selected }) {
  return (
    <div className={`${baseCard} ${selected ? "ring-2 ring-blue-500" : ""}`} style={{ borderColor: "#8B5CF6", background: "#FAF5FF" }}>
      <Handle type="target" position={Position.Top} className="!bg-violet-500" />
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-violet-100 flex items-center justify-center">
          <CheckCircle2 className="h-4 w-4 text-violet-600" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center">Diagnosis<RequiredBadge show={data.required} /></div>
          <div className="text-[13px] font-semibold text-slate-900 truncate">{data.label || "Diagnosis"}</div>
        </div>
      </div>
      {data.recommendation && (
        <div className="mt-1.5 text-[11.5px] text-slate-600">{data.recommendation}</div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-violet-500" />
    </div>
  );
}

export function RecommendProductNode({ data, selected }) {
  const { items: pricebookItems } = usePricebook();
  const productIds = (data.productIds || []).slice(0, 8);
  const items = productIds.map((id) => {
    const match = pricebookItems.find((p) => p.id === id);
    return match ? { ...match, found: true } : { id, found: false };
  });

  return (
    <div className={`${baseCard} ${selected ? "ring-2 ring-blue-500" : ""}`} style={{ borderColor: "#0EA5E9", minWidth: 240 }}>
      <Handle type="target" position={Position.Top} className="!bg-sky-500" />
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-sky-50 flex items-center justify-center">
          <ShoppingCart className="h-4 w-4 text-sky-600" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">Recommend Product</div>
        </div>
      </div>
      <div className="mt-1.5 text-[13px] font-semibold text-slate-900">{data.label || "Recommended products"}</div>
      {items.length === 0 ? (
        <div className="mt-2 text-[11px] text-slate-400 italic flex items-center gap-1">
          <Package className="h-3 w-3" /> No products selected — edit to add
        </div>
      ) : (
        <div className="mt-2 space-y-1.5">
          {items.map((p, i) => {
            const leftPct = ((i + 1) / (items.length + 1)) * 100;
            return (
              <div
                key={p.id}
                className={`relative flex items-center gap-1.5 border rounded px-1.5 py-1 ${
                  p.found ? "bg-sky-50/60 border-sky-100" : "bg-amber-50 border-amber-200"
                }`}
              >
                {p.found ? (
                  <>
                    <Package className="h-3 w-3 text-sky-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-medium text-slate-800 truncate">{p.name}</div>
                      <div className="text-[9.5px] text-slate-500 flex items-center gap-1">
                        {p.sku && <span className="font-mono">{p.sku}</span>}
                        {p.sku && <span>·</span>}
                        <span className="font-semibold text-emerald-700">${Number(p.unitPrice || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0" />
                    <div className="min-w-0 flex-1 text-[10.5px] text-amber-700 font-medium truncate">
                      Not in Pricebook ({p.id})
                    </div>
                  </>
                )}
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={p.id}
                  style={{ left: `${leftPct}%`, bottom: -8, background: "#0EA5E9", width: 8, height: 8 }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const nodeTypes = {
  startNode: StartNode,
  questionNode: QuestionNode,
  inputNode: InputNode,
  actionNode: ActionNode,
  diagnosisNode: DiagnosisNode,
  recommendProductNode: RecommendProductNode,
};
